import { useCallback } from 'react';
import { TFileWithType } from '@treezor/design-system';

import { useCreateUserContext } from '../../contexts/CreateUserContext';

import { useAppDispatch, useAppSelector } from '@/hooks/redux-helpers';

import { addUserKycDocument, addChildUserKycDocument } from '@/features/kyc/store/actions/kyc-profile.actions';

import { selectProfileKYCDocumentsByUserId } from '@/store/selectors';

export function useCreateUserKycDocuments(userId?: number) {
  const { user } = useCreateUserContext();

  const dispatch = useAppDispatch();

  const kycDocuments = useAppSelector((state) => selectProfileKYCDocumentsByUserId(state, userId)?.create);

  const isKycDocumentsUploaded =
    user?.kycDocuments?.length > 0
      ? kycDocuments?.state && Object.values(kycDocuments.state).every((state) => state.isLoading === false)
      : true;

  const isKycDocumentLoading = Object.values(kycDocuments?.state ?? {}).some((state) => state.isLoading === true);
  const onAddKycDocument = useCallback(
    (userId: number, document: TFileWithType, batchId?: string) => {
      dispatch(addUserKycDocument(userId, Number(document.type), document.file, `batch-${batchId}`, true));
    },
    [dispatch],
  );

  const onAddChildKycDocument = useCallback(
    (parentId: number, userId: number, document: TFileWithType, batchId?: string) => {
      dispatch(
        addChildUserKycDocument(parentId, userId, Number(document.type), document.file, `batch-${batchId}`, true),
      );
    },
    [dispatch],
  );

  const addKycDocuments = useCallback(
    (kycDocuments: TFileWithType[], userId: number) => {
      (kycDocuments || []).map((document, index) => {
        onAddKycDocument(userId, document, `${userId}-${index}`);
      });
    },
    [onAddKycDocument],
  );

  const addChildKycDocuments = useCallback(
    (kycDocuments: TFileWithType[], parentId: number, userId: number) => {
      (kycDocuments || []).map((document, index) => {
        onAddChildKycDocument(parentId, userId, document, `${userId}-${index}`);
      });
    },
    [onAddChildKycDocument],
  );

  const isKycDocumentsInvalid = useCallback((documents: TFileWithType[]) => {
    return (documents || []).some((document) => !document.file || !document.type);
  }, []);

  return {
    isKycDocumentsInvalid,
    addKycDocuments,
    isKycDocumentsUploaded,
    isKycDocumentLoading,
    addChildKycDocuments,
  };
}
